import { useState, useRef, useEffect } from 'react';
import type { PodcastWithRelations } from '../../../hooks/usePodcasts';

const useAudioPlayer = () => {
  const [currentPodcast, setCurrentPodcast] = useState<PodcastWithRelations | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [previousVolume, setPreviousVolume] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    audio.src = ''; // Initialize with empty source to prevent initial error

    const clearLoadingTimeout = () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
      clearLoadingTimeout();
      setLoadingAudio(false);
    });

    audio.addEventListener('canplay', () => {
      clearLoadingTimeout();
      setLoadingAudio(false);
    });

    audio.addEventListener('playing', () => {
      clearLoadingTimeout();
      setIsPlaying(true);
      setLoadingAudio(false);
    });

    audio.addEventListener('pause', () => {
      clearLoadingTimeout();
      setIsPlaying(false);
      setLoadingAudio(false);
    });

    audio.addEventListener('ended', () => {
      clearLoadingTimeout();
      setIsPlaying(false);
      setLoadingAudio(false);
      setCurrentTime(0);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
    });

    audio.addEventListener('error', () => {
      console.error('Erro ao reproduzir áudio:', audio.error);
      clearLoadingTimeout();
      if (audioRef.current) {
        audioRef.current.src = ''; // Clear source on error
      }
      setLoadingAudio(false);
      setIsPlaying(false);
      setCurrentPodcast(null);
    });

    audio.addEventListener('waiting', () => {
      setLoadingAudio(true);
      // Define um timeout de 10 segundos para o carregamento
      loadingTimeoutRef.current = setTimeout(() => {
        console.error('Timeout ao carregar áudio');
        setLoadingAudio(false);
        setIsPlaying(false);
        if (audioRef.current) {
          audioRef.current.src = '';
        }
        setCurrentPodcast(null);
      }, 10000);
    });

    return () => {
      clearLoadingTimeout();
      audio.pause();
      audio.src = ''; // Clear source on cleanup
      audio.remove();
    };
  }, []);

  const handlePlayPause = async (podcast: PodcastWithRelations) => {
    if (!audioRef.current) {
      console.error('Audio reference not available');
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      return;
    }

    // Validate podcast and audio URL
    if (!podcast?.audio_url) {
      console.error('Invalid podcast or missing audio URL:', podcast);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      return;
    }

    const audioUrl = podcast.audio_url.trim();
    
    if (!audioUrl) {
      console.error('Empty audio URL');
      setLoadingAudio(false);
      setIsPlaying(false);
      return;
    }

    // Validate URL format
    try {
      new URL(audioUrl);
    } catch (error) {
      console.error('Invalid audio URL format:', audioUrl);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      setLoadingAudio(false);
      setIsPlaying(false);
      return;
    }

    try {
      // Clear any existing loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }

      // Se clicar no mesmo podcast que está tocando
      if (currentPodcast?.id === podcast.id) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          setLoadingAudio(true);
          try {
            // Verify if the source is still valid
            const currentSrc = audioRef.current.src || '';
            const targetSrc = audioUrl;
            
            if (!currentSrc || currentSrc !== targetSrc) {
              console.log('Updating audio source from', currentSrc, 'to', targetSrc);
              audioRef.current.src = audioUrl;
              
              // Wait for audio to be loaded
              await new Promise((resolve, reject) => {
                const loadTimeout = setTimeout(() => {
                  reject(new Error('Audio load timeout'));
                }, 10000);

                if (!audioRef.current) {
                  clearTimeout(loadTimeout);
                  reject(new Error('Audio reference lost'));
                  return;
                }

                const handleCanPlay = () => {
                  clearTimeout(loadTimeout);
                  audioRef.current?.removeEventListener('canplaythrough', handleCanPlay);
                  audioRef.current?.removeEventListener('error', handleError);
                  resolve(true);
                };

                const handleError = () => {
                  clearTimeout(loadTimeout);
                  audioRef.current?.removeEventListener('canplaythrough', handleCanPlay);
                  audioRef.current?.removeEventListener('error', handleError);
                  reject(new Error('Failed to load audio'));
                };

                audioRef.current.addEventListener('canplaythrough', handleCanPlay);
                audioRef.current.addEventListener('error', handleError);
              });
            }

            await audioRef.current.play();
            setIsPlaying(true);
          } catch (error) {
            console.error('Error resuming playback:', error);
            if (audioRef.current) {
              audioRef.current.src = '';
            }
            setLoadingAudio(false);
            setIsPlaying(false);
            setCurrentPodcast(null);
            throw error;
          }
        }
        return;
      }

      // Se for um novo podcast
      setLoadingAudio(true);
      setCurrentPodcast(podcast);
      setCurrentPodcast(podcast);
      setCurrentPodcast(podcast);

      try {
        // Para qualquer reprodução atual
        audioRef.current.pause();
        audioRef.current.src = ''; // Clear previous source
        setIsPlaying(false);

        // Configura o novo podcast
        console.log('Setting new audio source:', audioUrl);
        audioRef.current.src = audioUrl;
        setCurrentPodcast(podcast);

        // Wait for audio to be loaded before playing
        await new Promise((resolve, reject) => {
          const loadTimeout = setTimeout(() => {
            reject(new Error('Audio load timeout'));
          }, 10000);

          if (!audioRef.current) {
            clearTimeout(loadTimeout);
            reject(new Error('Audio reference lost'));
            return;
          }

          const handleCanPlay = () => {
            clearTimeout(loadTimeout);
            audioRef.current?.removeEventListener('canplaythrough', handleCanPlay);
            audioRef.current?.removeEventListener('error', handleError);
            resolve(true);
          };

          const handleError = () => {
            clearTimeout(loadTimeout);
            audioRef.current?.removeEventListener('canplaythrough', handleCanPlay);
            audioRef.current?.removeEventListener('error', handleError);
            reject(new Error('Failed to load audio'));
          };

          audioRef.current.addEventListener('canplaythrough', handleCanPlay);
          audioRef.current.addEventListener('error', handleError);
        });

        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Error playing new podcast:', error);
        setLoadingAudio(false);
        setCurrentPodcast(null);
        if (audioRef.current) {
          audioRef.current.src = '';
        }
        throw error; 
      }
    } catch (error) {
      console.error('Error handling playback:', error);
      setLoadingAudio(false);
      setIsPlaying(false);
      setCurrentPodcast(null);
      if (audioRef.current) {
        audioRef.current.src = '';
      }
      throw error;
    }
  };

  const handleTimeUpdate = (value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleVolumeChange = (value: number) => {
    if (audioRef.current) {
      audioRef.current.volume = value;
      setVolume(value);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (volume > 0) {
        setPreviousVolume(volume);
        handleVolumeChange(0);
      } else {
        handleVolumeChange(previousVolume);
      }
    }
  };

  return {
    currentPodcast,
    isPlaying,
    loadingAudio,
    handlePlayPause,
    audioControls: {
      currentTime,
      duration,
      volume,
      handleTimeUpdate,
      handleVolumeChange,
      toggleMute
    }
  };
};

export { useAudioPlayer }